(() => {
  const WHATSAPP_POSITION_KEY = 'wb-whatsapp-position-v1';
  const RECENT_SEARCHES_KEY = 'wb-recent-searches-v1';
  const MAX_RECENT_SEARCHES = 6;

  const readJson = (key, fallback) => {
    try {
      const value = JSON.parse(window.localStorage.getItem(key));
      return value ?? fallback;
    } catch (_error) {
      return fallback;
    }
  };

  const writeJson = (key, value) => {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (_error) {
      // Storage may be unavailable in privacy mode; the UI still works for this visit.
    }
  };

  const setupDraggableWhatsApp = () => {
    const host = document.querySelector('gty-whatsapp-chat-button');
    const wrapper = host?.shadowRoot?.querySelector('.gty-wacb-wrapper');
    if (!wrapper || wrapper.dataset.wbDraggable === 'true') return false;

    wrapper.dataset.wbDraggable = 'true';
    wrapper.style.cursor = 'grab';
    wrapper.style.touchAction = 'none';
    wrapper.style.userSelect = 'none';
    wrapper.querySelectorAll('a, img').forEach((element) => element.setAttribute('draggable', 'false'));
    wrapper.addEventListener('dragstart', (event) => event.preventDefault());

    const bottomReserve = () => document.body.classList.contains('product-custom-wig-order-form') ? 180 : 12;
    const clampPosition = (x, y) => {
      const rect = wrapper.getBoundingClientRect();
      const margin = 12;
      return {
        x: Math.min(Math.max(x, margin), Math.max(margin, window.innerWidth - rect.width - margin)),
        y: Math.min(
          Math.max(y, margin),
          Math.max(margin, window.innerHeight - rect.height - bottomReserve()),
        ),
      };
    };

    const applyPosition = (position) => {
      const next = clampPosition(position.x, position.y);
      wrapper.style.setProperty('left', `${next.x}px`, 'important');
      wrapper.style.setProperty('top', `${next.y}px`, 'important');
      wrapper.style.setProperty('right', 'auto', 'important');
      wrapper.style.setProperty('bottom', 'auto', 'important');
      return next;
    };

    const savedPosition = readJson(WHATSAPP_POSITION_KEY, null);
    if (savedPosition && Number.isFinite(savedPosition.x) && Number.isFinite(savedPosition.y)) {
      applyPosition(savedPosition);
    }

    let dragState = null;
    let suppressClick = false;

    wrapper.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const rect = wrapper.getBoundingClientRect();
      dragState = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        originX: rect.left,
        originY: rect.top,
        moved: false,
      };
      wrapper.style.cursor = 'grabbing';
      wrapper.setPointerCapture?.(event.pointerId);
    });

    wrapper.addEventListener('pointermove', (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      const deltaX = event.clientX - dragState.startX;
      const deltaY = event.clientY - dragState.startY;
      if (!dragState.moved && Math.hypot(deltaX, deltaY) < 5) return;

      dragState.moved = true;
      event.preventDefault();
      applyPosition({ x: dragState.originX + deltaX, y: dragState.originY + deltaY });
    });

    const finishDrag = (event) => {
      if (!dragState || event.pointerId !== dragState.pointerId) return;
      wrapper.releasePointerCapture?.(event.pointerId);
      wrapper.style.cursor = 'grab';

      if (dragState.moved) {
        const rect = wrapper.getBoundingClientRect();
        writeJson(WHATSAPP_POSITION_KEY, { x: Math.round(rect.left), y: Math.round(rect.top) });
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 0);
      }
      dragState = null;
    };

    wrapper.addEventListener('pointerup', finishDrag);
    wrapper.addEventListener('pointercancel', finishDrag);

    let mouseDragState = null;
    const beginMouseDrag = (event) => {
      if (event.button !== 0) return;
      const rect = wrapper.getBoundingClientRect();
      mouseDragState = {
        startX: event.clientX,
        startY: event.clientY,
        originX: rect.left,
        originY: rect.top,
        moved: false,
      };
      wrapper.style.cursor = 'grabbing';
    };
    wrapper.addEventListener('mousedown', beginMouseDrag);
    document.addEventListener('mousedown', (event) => {
      if (!event.composedPath().includes(host)) return;
      beginMouseDrag(event);
    }, true);

    window.addEventListener('mousemove', (event) => {
      if (!mouseDragState) return;
      const deltaX = event.clientX - mouseDragState.startX;
      const deltaY = event.clientY - mouseDragState.startY;
      if (!mouseDragState.moved && Math.hypot(deltaX, deltaY) < 5) return;

      mouseDragState.moved = true;
      event.preventDefault();
      applyPosition({ x: mouseDragState.originX + deltaX, y: mouseDragState.originY + deltaY });
    }, { passive: false });

    window.addEventListener('mouseup', () => {
      if (!mouseDragState) return;
      wrapper.style.cursor = 'grab';
      if (mouseDragState.moved) {
        const rect = wrapper.getBoundingClientRect();
        writeJson(WHATSAPP_POSITION_KEY, { x: Math.round(rect.left), y: Math.round(rect.top) });
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 0);
      }
      mouseDragState = null;
    });

    let touchDragState = null;
    const beginTouchDrag = (event) => {
      const touch = event.changedTouches[0];
      if (!touch) return;
      const rect = wrapper.getBoundingClientRect();
      touchDragState = {
        identifier: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        originX: rect.left,
        originY: rect.top,
        moved: false,
      };
    };
    wrapper.addEventListener('touchstart', beginTouchDrag, { passive: true });
    document.addEventListener('touchstart', (event) => {
      if (!event.composedPath().includes(host)) return;
      beginTouchDrag(event);
    }, { capture: true, passive: true });

    window.addEventListener('touchmove', (event) => {
      if (!touchDragState) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === touchDragState.identifier);
      if (!touch) return;
      const deltaX = touch.clientX - touchDragState.startX;
      const deltaY = touch.clientY - touchDragState.startY;
      if (!touchDragState.moved && Math.hypot(deltaX, deltaY) < 5) return;

      touchDragState.moved = true;
      event.preventDefault();
      applyPosition({ x: touchDragState.originX + deltaX, y: touchDragState.originY + deltaY });
    }, { passive: false });

    window.addEventListener('touchend', (event) => {
      if (!touchDragState) return;
      const touch = Array.from(event.changedTouches).find((item) => item.identifier === touchDragState.identifier);
      if (!touch) return;
      if (touchDragState.moved) {
        const rect = wrapper.getBoundingClientRect();
        writeJson(WHATSAPP_POSITION_KEY, { x: Math.round(rect.left), y: Math.round(rect.top) });
        suppressClick = true;
        window.setTimeout(() => { suppressClick = false; }, 0);
      }
      touchDragState = null;
    });

    wrapper.addEventListener('click', (event) => {
      if (!suppressClick) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    window.addEventListener('resize', () => {
      const current = readJson(WHATSAPP_POSITION_KEY, null);
      if (!current) return;
      writeJson(WHATSAPP_POSITION_KEY, applyPosition(current));
    });

    return true;
  };

  const setupRecentSearches = () => {
    const panel = document.querySelector('[data-wb-recent-searches]');
    const form = document.querySelector('.template-search__search form[role="search"]');
    const input = form?.querySelector('input[name="q"]');
    if (!panel || !form || !input) return;

    const list = panel.querySelector('[data-wb-recent-searches-list]');
    const clearButton = panel.querySelector('[data-wb-recent-searches-clear]');
    const rootPath = window.Shopify?.routes?.root || '/';

    const getSearches = () => {
      const values = readJson(RECENT_SEARCHES_KEY, []);
      return Array.isArray(values) ? values.filter((value) => typeof value === 'string' && value.trim()) : [];
    };

    const saveSearch = (term) => {
      const clean = term.trim().replace(/\s+/g, ' ').slice(0, 80);
      if (!clean) return;
      const searches = getSearches().filter((value) => value.toLocaleLowerCase() !== clean.toLocaleLowerCase());
      searches.unshift(clean);
      writeJson(RECENT_SEARCHES_KEY, searches.slice(0, MAX_RECENT_SEARCHES));
    };

    const render = () => {
      const searches = getSearches();
      list.replaceChildren();
      panel.hidden = searches.length === 0;

      searches.forEach((term) => {
        const link = document.createElement('a');
        link.className = 'wb-recent-searches__chip';
        link.href = `${rootPath}search?q=${encodeURIComponent(term)}&options%5Bprefix%5D=last`;
        link.textContent = term;
        list.appendChild(link);
      });
    };

    const currentTerm = new URL(window.location.href).searchParams.get('q');
    if (currentTerm) saveSearch(currentTerm);

    form.addEventListener('submit', () => saveSearch(input.value));
    clearButton?.addEventListener('click', () => {
      writeJson(RECENT_SEARCHES_KEY, []);
      render();
      input.focus();
    });

    render();
  };

  setupRecentSearches();

  if (!setupDraggableWhatsApp()) {
    let attempts = 0;
    const widgetTimer = window.setInterval(() => {
      attempts += 1;
      if (setupDraggableWhatsApp() || attempts >= 120) window.clearInterval(widgetTimer);
    }, 250);
  }
})();
